/**
 * The Árvore chart's `Caminho` bar (Figma `320:11540`): the selected node's ancestry as
 * chips, `TeamActions` for the selected team after the last chip, and `Limpar` to clear
 * the selection. Chips and `Limpar` are absent -- not disabled -- with nothing selected,
 * the same rule `TeamActions` itself already follows for a caller without the grant.
 *
 * Its own tiny catalogue rather than a `campaign/i18n` entry: that catalogue's file is
 * another task's open write, and `defineCatalogue` exists precisely so an area never has
 * to queue behind one shared file for three strings (see `control/panel/tree.messages.ts`
 * for the same call).
 */
import { ChevronRight, X } from 'lucide-react';
import type { ReactNode } from 'react';

import { defineCatalogue } from '../../../i18n';
import { Badge } from '../../../ui/badge';
import { Button } from '../../../ui/button';
import { TeamActions } from './TeamActions';
import type { TeamOut } from './schemas';

const pathCatalogue = defineCatalogue(
  {
    title: 'Caminho',
    empty: 'Nenhuma equipe selecionada.',
    clear: 'Limpar',
  },
  {
    title: 'Path',
    empty: 'No team selected.',
    clear: 'Clear',
  },
);
const tp = pathCatalogue.t;

export type TreePathProps = {
  /** Absent in a test that drives `TreePath` with no campaign to call back to
   *  (`TeamActions` needs a real one); present whenever `Tree` reaches this from a route. */
  campaignId?: string;
  /** Root-to-selected ancestry, empty with nothing selected. */
  path: TeamOut[];
  onClear: () => void;
};

export function TreePath({ campaignId, path, onClear }: TreePathProps): ReactNode {
  const selected = path.length > 0 ? path[path.length - 1]! : null;

  return (
    <div data-slot="tree-path" className="flex flex-wrap items-center gap-2 border-b px-4 py-3">
      <span className="text-eyebrow text-muted-foreground">{tp('title')}</span>
      {selected === null ? (
        <span className="text-caption text-muted-foreground">{tp('empty')}</span>
      ) : (
        <ol className="flex flex-wrap items-center gap-2" aria-label={tp('title')}>
          {path.map((team, index) => (
            <li key={team.id} className="flex items-center gap-2">
              {index > 0 && <ChevronRight aria-hidden className="size-3 text-muted-foreground" />}
              <Badge
                variant="outline"
                className="gap-1 border-transparent bg-primary-quiet text-primary-quiet-foreground"
              >
                {/* A weight difference reads as two parts without axe's 4.5:1 catching an
                 *  opacity dim -- the team name is the identifying text, never faded. */}
                <span className="font-normal">{team.level_name}</span>
                <span className="font-medium">{team.name}</span>
              </Badge>
            </li>
          ))}
        </ol>
      )}
      {selected !== null && campaignId !== undefined && (
        <TeamActions campaignId={campaignId} team={selected} />
      )}
      <div className="flex-1" />
      {selected !== null && (
        <Button type="button" variant="secondary" size="sm" onClick={onClear}>
          <X aria-hidden className="size-3" />
          {tp('clear')}
        </Button>
      )}
    </div>
  );
}
